import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { Login } from './login.model';
import { AuthServerProvider } from '../auth/auth-session.service';
import { environment } from '../../../environments/environment';
import { SessionStorageService } from 'ngx-webstorage';
import { UserService } from '../../services/user.service';
import { Router } from '@angular/router';
import { StateStorageService } from '../auth/state-storage.service';
import { AuthData } from '../auth/auth.model';
// import * as psl from 'psl';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { switchMap } from 'rxjs/operators';
import { AuthService } from '@auth0/auth0-angular';
import { LoaderService } from '@services/loader.service';

export const LOGOUT_URL = environment.serverBaseUrl + 'logout';
export interface TokenExchangeResponse {
  user_id: number;
  atlas_token: string;
  user_profile_id: number;
}

@Injectable({ providedIn: 'root' })
export class LoginService {
  private readonly baseUrl;

  // to check and reload creditsList in case of impersonate
  private isImpersonate = new BehaviorSubject<boolean>(false);

  get reloadHeader(): Observable<boolean> {
    return this.isImpersonate.asObservable();
  }

  constructor(
    private userService: UserService,
    private authServerProvider: AuthServerProvider,
    private sessionStorage: SessionStorageService,
    private stateStorageService: StateStorageService,
    private router: Router,
    private http: HttpClient,
    public auth: AuthService,
    private loaderService: LoaderService
  ) {
    this.baseUrl = environment.serverBaseUrl;
  }

  login(credentials: Login): Observable<string> {
    /* php-integration-block
     * Login has been slightly modified for the Atlas + PHP Hibrid version.
     * Authentication is performed by the PHP backend, not the normal APIs
     */
    return this.authServerProvider.login(credentials).pipe(
      tap(authData => this.storeAuthData(authData)),
      tap(authData => this.storeCredentialsIfNotValidated(authData, credentials)),
      mergeMap(() => this.userService.identity(true))
    );
    /* php-integration-block-end */
  }

  public storeCredentialsIfNotValidated(authData: AuthData, credentials: Login) {
    if (authData.userStatus === 'REGISTERED') {
      // not yet validated
      this.sessionStorage.store('wsb-temp-credentials', credentials);
      this.router.navigate(['register']);
    } else {
      this.sessionStorage.clear('wsb-temp-credentials');
    }
  }

  public storeAuthData(authData: AuthData, externalAuth = false) {
    this.sessionStorage.clear('wsb-impersonate-subuser-key');
    this.sessionStorage.store('wsb-user-key', authData.idUser);
    this.sessionStorage.store('wsb-userprofile-key', authData.idUserProfile);
    this.sessionStorage.store('wsb-access-token', authData.token);
    this.sessionStorage.store('wsb-userprofile-role', authData.role && authData.role.id ? authData.role.id : authData.idRole);
    this.sessionStorage.store('wsb-user-status', authData.userStatus);
    this.sessionStorage.store('wsb-user-login', authData.wsbLogin);
    if (authData.url) {
      this.stateStorageService.storeUrl(authData.url);
    }
    if (authData.impersonator) {
      this.sessionStorage.store('wsb-igo-impersonator', authData.impersonator);
    }
    if (externalAuth) {
      this.sessionStorage.store('wsb-redirect-login', true);
    }
    // this.storeRemoveAuthDataForExternalPlatforms(authData);
  }

  // private storeRemoveAuthDataForExternalPlatforms(authData: AuthData, remove = false) {
  //   // External platforms need cookies for domain
  //   const parsed = psl.parse(window.location.hostname);
  //   const parsedDomain = parsed.domain;
  //   let cookieName = 'user_id';
  //   let cookieValue = '' + authData.idUser;
  //   document.cookie = cookieName + '=' + cookieValue + ';domain=.' + parsedDomain + ';path=/';
  //   cookieName = 'user_profile_id';
  //   cookieValue = '' + authData.idUserProfile;
  //   document.cookie = cookieName + '=' + cookieValue + ';domain=.' + parsedDomain + ';path=/';
  //   cookieName = 'access_token';
  //   cookieValue = '' + authData.token;
  //   document.cookie = cookieName + '=' + cookieValue + ';domain=.' + parsedDomain + ';path=/';
  // }

  impersonateUser(sessionKey: string, externalAuth = false): Observable<any | null> {
    return this.authServerProvider.impersonateUser(sessionKey).pipe(
      tap(authData => this.storeAuthData(authData, externalAuth)),
      mergeMap(() => this.userService.identity(true))
    );
  }

  logoutUrl(): string {
    return LOGOUT_URL;
  }

  logoutInClient(): void {
    this.userService.authenticate(null);
  }

  logout(redirect = true): void {
    this.userService.authenticate(null);
    this.sessionStorage.clear('wsb-user-key');
    this.sessionStorage.clear('wsb-user-login');
    this.sessionStorage.clear('wsb-userprofile-key');
    this.sessionStorage.clear('wsb-access-token');
    this.sessionStorage.clear('wsb-igo-impersonator');
    this.sessionStorage.clear('wsb-userprofile-role');
    this.sessionStorage.clear('wsb-impersonate-subuser-key');
    this.sessionStorage.clear('wsb-user-status');
    this.sessionStorage.clear('wsb-temp-credentials');
    this.sessionStorage.clear('previousUrl');
    this.sessionStorage.clear('hide-privacy-setting-session');
    this.sessionStorage.clear('wsb-userprofile-sessionKey');
    // if (redirect) {
    //   this.redirectToLogin();
    // }
    if (this.isUniversalLogin()) {
      let options = {} as any;
      if (redirect) {
        options.logoutParams = { returnTo: window.location.origin };
      }
      this.auth.logout(options);
    } else {
      if (redirect) {
        this.redirectToLogin();
      }
    }
  }

  destroySession(): void {
    try {
      this.http.post(LOGOUT_URL + '/session', {}).subscribe(() => {});
    } catch (e) {
      console.error('logout-> destroy session', e);
    }
  }

  clearAllCookies(): void {
    // Used for RCP logout
    const cookies = document.cookie.split('; ');
    for (const cookie of cookies) {
      const d = window.location.hostname.split('.');
      while (d.length > 0) {
        const cookieBase =
          encodeURIComponent(cookie.split(';')[0].split('=')[0]) +
          '=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=' +
          d.join('.') +
          ' ;path=';
        const p = location.pathname.split('/');
        document.cookie = cookieBase + '/';
        while (p.length > 0) {
          document.cookie = cookieBase + p.join('/');
          p.pop();
        }
        d.shift();
      }
    }
  }

  impersonateSubuser(subuserId: string) {
    this.sessionStorage.store('wsb-impersonate-subuser-key', subuserId);
    this.userService.identity(true);
    this.isImpersonate.next(true);
    this.router.navigate(['/dashboard']);
  }

  deImpersonateSubuser() {
    this.sessionStorage.clear('wsb-impersonate-subuser-key');
    this.sessionStorage.clear('hide-privacy-popup-impersonate-sub');
    this.userService.identity(true);
    this.isImpersonate.next(true);
    this.router.navigate(['/reseller/subaccounts']);
  }

  checkPassword(password): Observable<any> {
    const endpoint = this.isUniversalLogin() ? 'login/validate-password-by-auth0' : 'login';
    return this.userService.getAuthenticationState().pipe(
      switchMap(user => {
        const username = user.profile.registrationEmail;
        const body = {
          username,
          password,
          ignoreThisRequest: true
        };
        const headers = new HttpHeaders({
          interceptorIgnore: 'true'
        });
        return this.http.post(this.baseUrl + endpoint, body, { headers });
      })
    );
  }

  isUniversalLogin() {
    const isUniversalLogin = this.sessionStorage.retrieve('use-universal-login');
    return isUniversalLogin;
  }

  redirectToLogin() {
    this.loaderService.hide();
    if (this.isUniversalLogin()) {
      this.auth.loginWithRedirect();
    } else {
      this.router.navigate(['login']);
    }
  }

  getTokenExchange(): Observable<TokenExchangeResponse> {
    return this.http.post<TokenExchangeResponse>(this.baseUrl + 'login/auth0-token-exchange', {});
  }
}
